import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { Bell, CheckCircle, CircleStop, Copy, Eye, FileText, PackageCheck, Paperclip, Pencil, RefreshCcw, Search, Trash2, Undo2, XCircle } from "lucide-react";
import { toast } from "react-toastify";

import Fetching from "@/components/Fetching/Fetching";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";

import { useDownloadPdf } from "@/hooks/planning/application-order/appOrdersDocs";
import { oaKey, useOAApprove, useOACheckStatus, useOADelete, useOADuplicate, useOAFinish, useOAReject, useOAReturnState } from "@/hooks/planning/oa/useOA";

import { hasPermission, validRoles } from "@/services/Utils";

import useOARouteActions from "../../hooks/routing/useOARouteActions";
import { OA_ROUTE_ACTIONS } from "../../oa-query-schema";

import { ORDER_APPLICATION_CONFIRM_MESSAGE_BY_ACTION } from "./oa-actions.constants";
import { includes } from "lodash";

const OAActionsDropdown = ({ item, navigate, permissions = [] }) => {
	const queryClient = useQueryClient();
	const [confirmState, setConfirmState] = useState({ open: false, action: "" });
	const { openOrderActionModal, openDocuments } = useOARouteActions();

	const userRoles = JSON.parse(localStorage.getItem("roles") || "[]");
	const isAdmin = validRoles(userRoles, ["administrador"]);

	const { mutate: approve, isLoading: isApproving } = useOAApprove();
	const { mutate: reject, isLoading: isRejecting } = useOAReject();
	const { mutate: finish, isLoading: isFinishing } = useOAFinish();
	const { mutate: duplicate, isLoading: isDuplicating } = useOADuplicate();
	const { mutate: returnState, isLoading: isReturning } = useOAReturnState();
	const { mutate: checkStatus, isLoading: isCheckingStatus } = useOACheckStatus();
	const { mutate: remove, isLoading: isDeleting } = useOADelete();
	const { mutate: downloadPDF, isLoading: isDownloadingPDF } = useDownloadPdf(item.code);

	const canUsePermission = useCallback(
		(permissionName) => {
			if (!Array.isArray(permissions) || permissions.length === 0) {
				return true;
			}

			return hasPermission(permissionName, permissions);
		},
		[permissions],
	);

	const updateOAStateInCache = useCallback(
		(oaId, newStateId, newStateName) => {
			queryClient.setQueriesData(oaKey, (oldData) => {
				if (!oldData?.data) return oldData;
				return {
					...oldData,
					data: oldData.data.map((row) => {
						const isTargetRow = Number(row.id) === Number(oaId) || Number(row.app_order_id) === Number(oaId);
						if (!isTargetRow) return row;

						return {
							...row,
							state_id: newStateId,
							state_name: newStateName,
							application_orders_state_id: Number(newStateId || 0),
							application_orders_state_name: newStateName || row.application_orders_state_name,
						};
					}),
				};
			});
		},
		[queryClient],
	);

	const removeOAFromCache = useCallback(
		(oaId) => {
			queryClient.setQueriesData(oaKey, (oldData) => {
				if (!oldData?.data) return oldData;
				return {
					...oldData,
					data: oldData.data.filter((row) => Number(row.id) !== Number(oaId)),
				};
			});
		},
		[queryClient],
	);

	const runOAStateAction = useCallback(
		(mutateFunction, successMessage, errorMessage) => {
			mutateFunction(
				{ oa_id: item.app_order_id },
				{
					onSuccess: (data) => {
						if (successMessage) toast.success(successMessage);
						updateOAStateInCache(data.oa_id, data.state_id, data.state_name);
						queryClient.invalidateQueries(oaKey);
					},
					onError: (mutationError) => {
						const apiMessages = mutationError?.response?.data?.messages;
						const serverMessage =
							Array.isArray(apiMessages) && apiMessages.length > 0 ? apiMessages.join("\n") : mutationError?.response?.data?.error;
						toast.error(serverMessage || errorMessage || "Error al ejecutar la acción.");
					},
				},
			);
		},
		[item.app_order_id, queryClient, updateOAStateInCache],
	);

	const runOADeleteAction = useCallback(() => {
		remove(
			{ oa_id: item.app_order_id },
			{
				onSuccess: (data) => {
					toast.success(`Se eliminó la OA N° ${item.code}.`);
					removeOAFromCache(data.oa_id);
				},
				onError: (mutationError) => {
					const apiMessages = mutationError?.response?.data?.messages;
					const serverMessage = Array.isArray(apiMessages) && apiMessages.length > 0 ? apiMessages.join("\n") : mutationError?.response?.data?.error;
					toast.error(serverMessage || "Error al eliminar la OA.");
				},
			},
		);
	}, [item.app_order_id, item.code, remove, removeOAFromCache]);

	const handleDownloadPDF = useCallback(() => {
		downloadPDF({ code: item.app_order_id });
	}, [downloadPDF, item.app_order_id]);

	const handleOpenOrderActionModal = useCallback(
		(action) => {
			openOrderActionModal(action, item.app_order_id);
		},
		[item.app_order_id, openOrderActionModal],
	);

	const handleShowDocuments = useCallback(() => {
		openDocuments(item.app_order_id);
	}, [item.app_order_id, openDocuments]);

	const openConfirm = useCallback((action) => setConfirmState({ open: true, action }), []);
	const closeConfirm = useCallback(() => setConfirmState({ open: false, action: "" }), []);

	const handleConfirmAction = useCallback(() => {
		const confirmActions = {
			return_state: () => runOAStateAction(returnState, `Se volvió un estado atrás la OA N° ${item.code}.`, "Error al volver un estado atrás."),
			approve: () => runOAStateAction(approve, `Se aprobó la OA N° ${item.code}.`, "Error al aprobar la OA."),
			reject: () => runOAStateAction(reject, `Se rechazó la OA N° ${item.code}.`, "Error al rechazar la OA."),
			delete: () => runOADeleteAction(),
			finish: () => runOAStateAction(finish, `Se finalizó la OA N° ${item.code}.`, "Error al finalizar la OA."),
			duplicate: () =>
				duplicate(
					{ app_order_ids: [item.app_order_id] },
					{
						onSuccess: (response) => {
							const newCode = response?.new_codes?.[0];
							toast.success(`Se ha duplicado correctamente. Nueva OA N° ${newCode}`);
							queryClient.invalidateQueries(oaKey);
						},
						onError: () => {
							toast.error("Error al duplicar la OA. Por favor intente nuevamente.");
						},
					},
				),
		};

		const executeAction = confirmActions[confirmState.action];
		if (typeof executeAction === "function") {
			executeAction();
		}

		closeConfirm();
	}, [approve, closeConfirm, confirmState.action, duplicate, finish, item, queryClient, reject, runOADeleteAction, runOAStateAction, returnState]);

	const menuItems = useMemo(() => {
		const items = [
			{
				label: "Vista previa",
				icon: Eye,
				action: () => handleOpenOrderActionModal("vista_previa"),
			},
			{
				label: "Generar PDF",
				icon: FileText,
				action: handleDownloadPDF,
			},
			{
				label: "Ver Adjuntos",
				icon: Paperclip,
				action: handleShowDocuments,
			},
		];

		if (canUsePermission("edit")) {
			items.push(
				{
					label: "Crear copia",
					icon: Copy,
					action: () => openConfirm("duplicate"),
				},
				{
					label: "Editar",
					icon: Pencil,
					action: () => navigate(`/orden_aplicacion/editar/${item.app_order_id}`),
				},
			);
		}

		items.push({ separator: true });

		if (canUsePermission("edit") && [2, 4].includes(Number(item.application_orders_state_id))) {
			items.push({
				label: "Finalizar",
				icon: CircleStop,
				action: () => openConfirm("finish"),
			});
		}

		if (canUsePermission("edit") && includes([2, 4], Number(item.application_orders_state_id))) {
			items.push({
				label: "Consumir productos",
				icon: PackageCheck,
				action: () => handleOpenOrderActionModal("consumir_productos"),
			});
		}

		if (Number(item.application_orders_state_id) === 1 && canUsePermission("approve")) {
			items.push(
				{
					label: "Aprobar",
					icon: CheckCircle,
					action: () => openConfirm("approve"),
				},
				{
					label: "Rechazar",
					icon: XCircle,
					action: () => openConfirm("reject"),
					danger: true,
				},
			);
		}

		if (canUsePermission("edit")) {
			items.push(
				{
					label: "Volver un estado atrás",
					icon: Undo2,
					action: () => openConfirm("return_state"),
				},
				{
					label: "Recalcular precios",
					icon: RefreshCcw,
					action: () => handleOpenOrderActionModal(OA_ROUTE_ACTIONS.recalculatePrices),
				},
			);
		}

		if (isAdmin) {
			items.push({
				label: "Check Estado",
				icon: Search,
				action: () => runOAStateAction(checkStatus, "Estado verificado correctamente.", "Error al verificar el estado."),
			});
		}

		items.push(
			{ separator: true },
			{
				label: "Dar Aviso de Aplicación",
				icon: Bell,
				action: () => handleOpenOrderActionModal("dar_aviso"),
			},
		);

		if (canUsePermission("delete")) {
			items.push(
				{ separator: true },
				{
					label: "Eliminar",
					icon: Trash2,
					action: () => openConfirm("delete"),
					danger: true,
				},
			);
		}

		return items;
	}, [
		canUsePermission,
		checkStatus,
		handleDownloadPDF,
		handleOpenOrderActionModal,
		handleShowDocuments,
		isAdmin,
		item,
		navigate,
		openConfirm,
		runOAStateAction,
	]);

	const isLoading = isApproving || isRejecting || isFinishing || isDeleting || isDownloadingPDF || isDuplicating || isReturning || isCheckingStatus;

	return (
		<>
			{isLoading ? <Fetching topMargin="0px" /> : <CustomDropdown title="Acciones" items={menuItems} align="right" />}
			<CustomConfirmModal
				open={confirmState.open}
				onClose={closeConfirm}
				onConfirm={handleConfirmAction}
				title="Confirmar acción"
				message={
					typeof ORDER_APPLICATION_CONFIRM_MESSAGE_BY_ACTION[confirmState.action] === "function"
						? ORDER_APPLICATION_CONFIRM_MESSAGE_BY_ACTION[confirmState.action](item.code)
						: ORDER_APPLICATION_CONFIRM_MESSAGE_BY_ACTION[confirmState.action] || "¿Desea continuar?"
				}
				confirmText="Sí, continuar"
				cancelText="Cancelar"
				isLoading={isLoading}
			/>
		</>
	);
};

export default OAActionsDropdown;
